import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { SetShowLoader, ShowToastrAlert } from 'app/modules/shared-state/shared.actions';
import {
    OnRemoveSharedGridRowData,
    OnSetSharedGridRowData,
} from 'app/modules/shared/ag-grid/shared-grid-state/shared-grid.actions';
import { catchError, tap } from 'rxjs';
import { DropdownOptionsService } from '../dropdown-options.service';
import {
    OnDeleteType,
    OnDeleteValue,
    OnGetTypes,
    OnGetDropDownValues,
    OnSetNewDropdownData,
    OnSubmitNewType,
    SetCurrentType,
} from './dropdown.actions';
import { DisplayValue, DropdownStateModel } from './dropdown.model';
import { SharedGridState } from 'app/modules/shared/ag-grid/shared-grid-state/shared-grid.state';

@State<DropdownStateModel>({
    name: 'dropdownState',
    defaults: {
        displayValues: undefined,
        displayTypes: undefined,
        newDropdownData: undefined,
        currentType: '',
        dropdownForm: {
            model: {
                type: '',
            },
            dirty: false,
            status: '',
            errors: {},
        },
    },
})
@Injectable()
export class DropdownState {
    private _store = inject(Store);
    private _dropdownService = inject(DropdownOptionsService);

    @Action(OnGetTypes)
    getTypes(ctx: StateContext<DropdownStateModel>, { payload }: OnGetTypes) {
        ctx.dispatch(new SetShowLoader(true));
        ctx.patchState({ currentType: '' });
        return this._dropdownService.getDropdownTypes().pipe(
            catchError((error) => {
                console.error('Error getting dropdown types:', error);
                ctx.dispatch(
                    new ShowToastrAlert({
                        showToast: true,
                        toastrType: 'error',
                        messageTitle: 'Error',
                        messageBody: `Error getting dropdown types: ${error.error.message ? error.error.message.message : `Error connecting server`}`,
                        position: 'toast-top-center',
                    })
                );
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap((result: any) => {
                ctx.patchState({ displayTypes: result });
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(
                    new OnSetSharedGridRowData({
                        reload: true,
                        payload: result,
                        gridUUID: payload.gridUUID,
                    })
                );
            })
        );
    }

    @Action(OnGetDropDownValues)
    async onGetSecondGridData(ctx: StateContext<DropdownStateModel>, { payload }: OnGetDropDownValues) {
        ctx.patchState({ currentType: payload.type });
        ctx.dispatch(new SetShowLoader(true));
        return this._dropdownService.getDropdownsByType(payload.type).pipe(
            catchError((error) => {
                console.error('Error getting dropdowns:', error);
                ctx.dispatch(
                    new ShowToastrAlert({
                        showToast: true,
                        toastrType: 'error',
                        messageTitle: 'Error',
                        messageBody: `Error getting dropdown values: ${error.error.message ? error.error.message.message : `Error connecting server`}`,
                        position: 'toast-top-center',
                    })
                );
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap((result: any) => {
                ctx.patchState({ displayValues: result });
                ctx.dispatch(new SetShowLoader(false));
                if (payload.gridUUID) {
                    ctx.dispatch(
                        new OnSetSharedGridRowData({
                            reload: true,
                            payload: result,
                            gridUUID: payload.gridUUID,
                        })
                    );
                }
            })
        );
    }

    @Action(OnDeleteType)
    onDeleteType(ctx: StateContext<DropdownStateModel>, { payload }: OnDeleteType) {
        ctx.dispatch(new SetShowLoader(true));
        return this._dropdownService.deleteDropdownType(payload.removeRow.type).pipe(
            catchError((error) => {
                console.error('Error deleting dropdown type:', error);
                ctx.dispatch(new ShowToastrAlert({
                    showToast: true,
                    toastrType: 'error',
                    messageTitle: 'Error',
                    messageBody: `Error deleting dropdown type: ${error.error.message ? error.error.message.message : `Error connecting server`}`,
                    position: 'toast-top-center',
                }));
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap((result: any) => {
                ctx.dispatch(new SetShowLoader(false));
                if (result.count != 0) {
                    ctx.dispatch(
                        new OnRemoveSharedGridRowData({
                            gridUUID: payload.gridUUID,
                            removeRow: payload.removeRow,
                        })
                    );
                    ctx.dispatch(
                        new OnSetSharedGridRowData({
                            reload: true,
                            gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).secondGridUUID,
                            payload: [],
                        })
                    );
                    ctx.dispatch(
                        new ShowToastrAlert({
                            showToast: true,
                            toastrType: 'success',
                            messageTitle: 'Success',
                            messageBody: `Dropdown value deleted successfully`,
                            position: 'toast-top-center',
                        })
                    );
                }
            })
        );
    }

    @Action(OnDeleteValue)
    onDeleteValue(ctx: StateContext<DropdownStateModel>, { payload }: OnDeleteType) {
        ctx.dispatch(new SetShowLoader(true));
        return this._dropdownService.deleteDropdownValue(payload.removeRow._id).pipe(
            catchError((error) => {
                console.error('Error deleting dropdown value:', error);
                ctx.dispatch(
                    new ShowToastrAlert({
                        showToast: true,
                        toastrType: 'error',
                        messageTitle: 'Error',
                        messageBody: `Error deleting dropdown value: ${error.error.message ? error.error.message.message : `Error connecting server`}`,
                        position: 'toast-top-center',
                    })
                );
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap((result: any) => {
                ctx.dispatch(new SetShowLoader(false));
                if (result.count != 0) {
                    ctx.dispatch(
                        new OnRemoveSharedGridRowData({
                            gridUUID: payload.gridUUID,
                            removeRow: payload.removeRow,
                        })
                    );
                }
                ctx.dispatch(
                    new ShowToastrAlert({
                        showToast: true,
                        toastrType: 'success',
                        messageTitle: 'Success',
                        messageBody: `Dropdown value deleted successfully`,
                        position: 'toast-top-center',
                    })
                );
            })
        );
    }

    @Action(OnSetNewDropdownData)
    onSetNewDropdownData(ctx: StateContext<DropdownStateModel>, { payload }: OnSetNewDropdownData) {
        ctx.dispatch(new SetShowLoader(true));
        const newDropdownData = [];
        for (const row of payload.rowData) {
            if (!row._id) {
                newDropdownData.push(row);
            }
        }
        ctx.patchState({ newDropdownData: newDropdownData });
        return this._dropdownService
            .addDropdownValues({ type: ctx.getState().currentType, value: newDropdownData })
            .pipe(
                catchError((error) => {
                    console.error('Error adding dropdown values:', error);
                    ctx.dispatch(
                        new ShowToastrAlert({
                            showToast: true,
                            toastrType: 'error',
                            messageTitle: 'Error',
                            messageBody: `Error saving new dropdowns: ${error.error.message ? error.error.message.message : `Error connecting server`}`,
                            position: 'toast-top-center',
                        })
                    );
                    ctx.dispatch(new SetShowLoader(false));
                    throw error;
                }),
                tap(() => {
                    ctx.dispatch(new SetShowLoader(false));
                    ctx.dispatch(new OnSetSharedGridRowData({
                        reload: true,
                        gridUUID: payload.gridUUID,
                        payload: [],
                    }));
                    ctx.dispatch(
                        new ShowToastrAlert({
                            showToast: true,
                            toastrType: 'success',
                            messageTitle: 'Success',
                            messageBody: `New dropdowns saved successfully`,
                            position: 'toast-top-center',
                        })
                    );
                })
            );
    }

    @Action(OnSubmitNewType)
    onSubmitNewType(ctx: StateContext<DropdownStateModel>, { payload }: OnSetNewDropdownData) {
        ctx.dispatch(new SetShowLoader(true));
        return this._dropdownService.addType({ type: ctx.getState().dropdownForm.model.type, value: null }).pipe(
            catchError((error) => {
                console.error('Error adding dropdown values:', error);
                ctx.dispatch(
                    new ShowToastrAlert({
                        showToast: true,
                        toastrType: 'error',
                        messageTitle: 'Error',
                        messageBody: `Error saving new dropdowns: ${error.error.message ? error.error.message.message : `Error connecting server`}`,
                        position: 'toast-top-center',
                    })
                );
                ctx.dispatch(new SetShowLoader(false));
                throw error;
            }),
            tap((result: any) => {
                ctx.patchState({ dropdownForm: { model: { type: '' }, dirty: false, status: '', errors: {} } });
                ctx.dispatch(new OnSetSharedGridRowData({ payload: [result], gridUUID: payload.gridUUID }));
                ctx.dispatch(new SetShowLoader(false));
                ctx.dispatch(
                    new OnSetSharedGridRowData({
                        reload: true,
                        gridUUID: this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).secondGridUUID,
                        payload: [],
                    })
                );
                ctx.dispatch(
                    new ShowToastrAlert({
                        showToast: true,
                        toastrType: 'success',
                        messageTitle: 'Success',
                        messageBody: `New dropdown type saved successfully`,
                        position: 'toast-top-center',
                    })
                );
            })
        );
    }

    @Selector()
    static getCurrentType(state: DropdownStateModel) {
        return state.currentType;
    }


    @Selector()
    static getDropDownValues(state: DropdownStateModel): DisplayValue[] {
        return state.displayValues;
    }
    // Action Handler for Setting Current Type
    @Action(SetCurrentType)
    setCurrentType(ctx: StateContext<DropdownStateModel>, action: SetCurrentType) {
        ctx.patchState({ currentType: action.payload });
    }

}
